import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";

// Sprint V1 · P0-1 : dès qu'un lien magique est consommé, on route vers
// l'URL du DOSSIER PRÉCIS retourné par le backend. On efface aussi tout état
// résiduel (localStorage/sessionStorage) pour éviter de mélanger deux dossiers.
const ClientInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      let sessionOk = false;
      let dossierId = null;
      try {
        const res = await api.clientMagicConsume(token);
        dossierId = res?.dossier_id || null;
        sessionOk = true;
      } catch (e1) {
        if (e1?.status === 401) {
          // Peut être : token déjà consommé (StrictMode) OU vraiment invalide.
          try {
            const sess = await api.clientSession();
            dossierId = sess?.dossier_id || null;
            sessionOk = true;
          } catch {
            // Dernier recours : JWT legacy 180j
            try {
              const legacy = await api.clientMagic(token);
              dossierId = legacy?.dossier_id || null;
              sessionOk = true;
            } catch { /* invalid */ }
          }
        } else if (e1?.status === 410) {
          setError("Ce lien est trop ancien. Demande un nouveau lien à ton conseiller.");
          return;
        }
      }
      if (!alive) return;
      if (sessionOk) {
        // Purge tout état lié à un ancien dossier
        try {
          sessionStorage.removeItem("wyvio.client.lastDossierId");
          localStorage.removeItem("wyvio.client.cache");
        } catch { /* ignore */ }
        if (dossierId) {
          sessionStorage.setItem("wyvio.client.lastDossierId", dossierId);
          navigate(`/client/dossier/${dossierId}`, { replace: true });
        } else {
          navigate("/client/welcome", { replace: true });
        }
      } else {
        setError("Ce lien est invalide ou a expiré");
      }
    })();
    return () => { alive = false; };
  }, [token, navigate]);

  return (
    <div
      data-testid="page-client-invite"
      className="min-h-screen relative overflow-hidden bg-[#F7F8FA]"
    >
      <div
        aria-hidden
        className="absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-[#C8FF2F]/25 blur-3xl"
      />

      <header className="relative z-10 max-w-md mx-auto px-5 h-14 flex items-center">
        <Logo variant="full" size="xs" rounded="rounded-md" />
      </header>

      <main className="relative z-10 max-w-md mx-auto px-5 pt-24 text-center">
        {!error ? (
          <div className="wyvio-fade-in">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#111111] text-[#C8FF2F] flex items-center justify-center">
              <Loader2 size={22} strokeWidth={2} className="animate-spin-slow" />
            </div>
            <p className="mt-6 text-[16px] font-semibold text-[#111111]">
              Ouverture de votre espace…
            </p>
            <p className="mt-2 text-[13px] text-[#6B7280]">
              Vérification du lien sécurisé.
            </p>
          </div>
        ) : (
          <div data-testid="invite-error" className="wyvio-fade-in">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center">
              <AlertCircle size={22} strokeWidth={2} />
            </div>
            <p className="mt-6 text-[16px] font-semibold text-[#111111]">
              Lien invalide
            </p>
            <p className="mt-2 text-[13px] text-[#6B7280]">
              {error}
            </p>
            <button
              onClick={() => navigate("/client/login")}
              className="mt-6 h-11 px-5 rounded-full bg-[#111111] text-white text-[13px] font-semibold hover:bg-black transition-colors"
            >
              Se connecter avec mon code
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientInvite;
